import { dedent } from 'ts-dedent'
import { DataWarehouseConnection } from '..'
import { Workflow } from '../..'
import { WorkflowComponent } from '../../../components/common/workflowcomponent'
import { ReadTable } from '../../../components/readtable'
import {
  ColumnDataType,
  ExecuteDynamicSQLParams,
  Provider,
  TableOptimizationColumnDataType,
  TableOptimizationInfo,
  WorkflowCodeType,
  WorkflowComponentParamType,
  WorkflowComponentValue,
  WorkflowEnvVars,
  WorkflowTableSchemas,
  WorkflowVariable
} from '../../../types'
import {
  OUTPUT_TABLE_FQN_VAR_NAME,
  OUTPUT_TABLE_NAME_VAR_NAME,
  WORKFLOW_OUTPUT_TABLE_NAME_PARAMETER_NAME,
  formatVariableValue,
  getVariablesUsedInQuery,
  hashString,
  isNumericIntegerValue
} from '../../../utils'

export class PGConnection extends DataWarehouseConnection {
  constructor(name: string, tempStoragePath: string) {
    super(name, tempStoragePath, Provider.Postgres)
  }

  getOutputTableName(
    workflowId: string,
    componentName: string,
    params: Record<string, unknown>,
    output: string,
    codeType: WorkflowCodeType
  ): string {
    return super
      .getOutputTableName(workflowId, componentName, params, output, codeType)
      .toLowerCase()
  }

  castValue(value: string, type: string) {
    return `(${value})::${type}`
  }

  getTempSelectQuery(select: string, dryRun: boolean): string {
    if (dryRun) {
      return dedent`SELECT * FROM
                (
                  ${select}
                ) t
                WHERE 1=0`
    } else {
      return select
    }
  }

  getCreateTempTableQuery(
    tablename: string,
    select: string,
    codeType: WorkflowCodeType,
    optimizeBy: TableOptimizationInfo | null,
    addOptimizationStats?: boolean
  ): string {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const temp =
      codeType === WorkflowCodeType.StoredProcedure ? 'TEMPORARY ' : ''
    select = dedent(select)
    tablename = this.quoteName(tablename)
    const indexname = `idx_${hashString(tablename)}`
    if (!dryRun && optimizeBy?.column) {
      let query = ''
      if (TableOptimizationColumnDataType.Geo === optimizeBy.column.type) {
        query = dedent`DROP TABLE IF EXISTS ${tablename};
                CREATE ${temp}TABLE ${tablename} AS
                ${this.getTempSelectQuery(select, dryRun)};
                DROP INDEX IF EXISTS ${indexname};
                CREATE INDEX ${indexname}
                ON ${tablename}
                USING GIST (${optimizeBy.column.name});`
      } else {
        query = dedent`DROP TABLE IF EXISTS ${tablename};
                CREATE ${temp}TABLE ${tablename} AS
                ${this.getTempSelectQuery(select, dryRun)};
                DROP INDEX IF EXISTS ${indexname};
                CREATE INDEX ${indexname}
                ON ${tablename} (${optimizeBy.column.name});`
      }
      if (optimizeBy?.pgCluster) {
        query += dedent`
                  CLUSTER ${tablename} USING ${indexname};`
      }
      return query
    } else {
      return dedent`DROP TABLE IF EXISTS ${tablename};
              CREATE ${temp}TABLE ${tablename} AS
              ${this.getTempSelectQuery(
                select,
                codeType === WorkflowCodeType.DryRun
              )};`
    }
  }

  getCreateTableQuery(
    tablename: string,
    select: string,
    codeType: WorkflowCodeType,
    optimizeBy: TableOptimizationInfo | null,
    addOptimizationStats?: boolean
  ): string {
    return this.getCreateTempTableQuery(
      tablename,
      select,
      codeType,
      optimizeBy,
      addOptimizationStats
    ).replace(RegExp('TEMPORARY ', 'g'), '')
  }

  getDropTableQuery(viewname: string): string {
    return `DROP TABLE IF EXISTS ${this.quoteName(viewname)} CASCADE;`
  }

  executeDynamicSQL({ sql, addQuotes, replacements }: ExecuteDynamicSQLParams) {
    const lines = sql
      .replace(/BEGIN\n([\s\S]*?)END;/, '$1')
      .split(';')
      .filter((s) => /\S/.test(s))
    if (replacements) {
      return lines
        .map(
          (q) => dedent`EXECUTE
                          ${replacements?.reduce(
                            (acc, [org, repl]) =>
                              dedent`REPLACE(
                                ${acc},
                                ${org},
                                ${repl}
                              )
                              `,
                            addQuotes
                              ? this.quoteMultilineString(dedent(q))
                              : dedent(q)
                          )};`
        )
        .join('\n')
    } else {
      return lines
        .map(
          (q) => dedent`EXECUTE
                          ${
                            addQuotes
                              ? this.quoteMultilineString(dedent(q))
                              : dedent(q)
                          }
                        ;`
        )
        .join('\n')
    }
  }

  wrapComponentCodeInFlowControlBlock(
    query: string,
    codeType: WorkflowCodeType
  ): string {
    if (codeType === WorkflowCodeType.DryRun) {
      return dedent`BEGIN
                ${query}
              EXCEPTION
              WHEN OTHERS THEN
                PERFORM 'SELECT 1';
                -- do nothing
              END;`
    } else {
      return dedent`BEGIN
                 ${query}
              END;`
    }
  }

  getStoredProcedureOutputTableNameExpression(
    workflow: Workflow,
    full: boolean
  ): string {
    const publicVars = workflow.variables.filter((v) => v.public)
    let root = workflow.getStoredProcedureOutputTableFullName(this)
    if (!full) {
      root = root.split('.').pop() as string
    }
    if (publicVars.length === 0) {
      return `'${root}_' || SUBSTRING(MD5(''), 1, 16)`
    } else {
      return `'${root}_' || SUBSTRING(MD5(ARRAY_TO_STRING(ARRAY[${publicVars
        .map((v) => this.quoteVariableName(v.name))
        .join(',')}], ',')), 1, 16)`
    }
  }

  wrapCodeInIfTableExistsBlock(
    query: string,
    outputTables: Array<string>,
    codeType: WorkflowCodeType,
    outputTablesExpressions?: Array<string>,
    variablePrefix?: string
  ): string {
    variablePrefix = variablePrefix || ''
    if (
      [WorkflowCodeType.DryRun, WorkflowCodeType.StoredProcedure].includes(
        codeType
      )
    ) {
      return query
    } else if (codeType === WorkflowCodeType.FullNoCache) {
      return dedent`
        ${outputTables
          .map((x) => `DROP TABLE IF EXISTS ${this.quoteName(x)};`)
          .join('\n')}
        ${query}
      `
    } else {
      const [database, schema] = this.tempStoragePath.toLowerCase().split('.')

      const expressionChecks: Array<string> = []
      for (const expression of outputTablesExpressions || []) {
        expressionChecks.push(dedent`
            ${variablePrefix}schema := SPLIT_PART(${expression}, '.', 2);
            ${variablePrefix}tableName := SPLIT_PART(${expression}, '.', 3);
            EXECUTE
              'SELECT
                COUNT(1) AS cnt
              FROM
                "${database}".information_schema.tables
              WHERE
                  table_name LIKE ''' || ${variablePrefix}tableName || '''
                  AND table_schema LIKE ''' || ${variablePrefix}schema || ''''
              INTO ${variablePrefix}tablesCount;
            ${variablePrefix}totalTablesCount := ${variablePrefix}tablesCount + ${variablePrefix}totalTablesCount;
        `)
      }
      const outputTablesLiteralNames = outputTables.map(
        (x) => `'''${(x.split('.').pop() as string).toLowerCase()}'''`
      )
      let literalNamesCheck = ''
      if (outputTablesLiteralNames.length > 0) {
        literalNamesCheck = dedent`
          ${variablePrefix}tablesNames := ${outputTablesLiteralNames};
          ${variablePrefix}query = '
            SELECT COUNT(1) AS cnt
            FROM "${database}".information_schema.tables
            WHERE
              table_name IN (' || ${variablePrefix}tablesNames || ')
              AND table_schema LIKE ''${schema}''
          ';
          EXECUTE ${variablePrefix}query INTO ${variablePrefix}tablesCount;
          ${variablePrefix}totalTablesCount := ${variablePrefix}tablesCount + ${variablePrefix}totalTablesCount;
        `
      }
      const totalTablesCount =
        outputTables.length + (outputTablesExpressions?.length || 0)
      return dedent`
          DECLARE
            ${variablePrefix}query TEXT;
            ${variablePrefix}tablesCount INTEGER;
            ${variablePrefix}totalTablesCount INTEGER DEFAULT 0;
            ${variablePrefix}tablesNames TEXT;
            ${variablePrefix}schema TEXT;
            ${variablePrefix}tableName TEXT;
          BEGIN
            ${expressionChecks.join('\n')}
            ${literalNamesCheck}
            IF (${variablePrefix}totalTablesCount < ${totalTablesCount}) THEN
              BEGIN
                ${query}
              END;
            END IF;
          END;`
    }
  }

  async getWorkflowCode(
    workflow: Workflow,
    envVars: WorkflowEnvVars,
    codeType: WorkflowCodeType,
    schemas?: WorkflowTableSchemas,
    preQuery?: string
  ): Promise<string> {
    const queries = await workflow.getSubqueries(
      this,
      envVars,
      codeType,
      schemas
    )
    const executable = queries.some((q) => q.query)
    let query = ''
    if (executable) {
      if (codeType !== WorkflowCodeType.StoredProcedure) {
        query = await this._getScriptBody(
          workflow,
          envVars,
          queries,
          codeType,
          schemas
        )
        query = dedent`/*
                ${workflow.workflowMetadataAsCommentString(codeType)}
                */
                DO $$
                BEGIN
                  ${preQuery || ''}${query}
                END;
                $$`
        query = this.replaceVariablesInScriptQuery(query, workflow.variables)
      } else {
        query = await this._getProcedureBody(
          workflow,
          envVars,
          queries,
          schemas
        )
      }
    }
    return query
  }

  replaceVariablesInStoredProcedureQuery(
    variables: Array<WorkflowVariable>,
    query: string,
    requiresDynamicSQL: boolean
  ) {
    const varnames = getVariablesUsedInQuery(query, variables)
    if (varnames.length === 0) {
      return query
    }
    if (requiresDynamicSQL) {
      query = query.replace(/'/g, "\\'").replace(/BEGIN\n([\s\S]*?)END;/, '$1')
      for (const varname of varnames) {
        query = query.replace(
          RegExp(`@${varname}`, 'g'),
          `' || ${this.quoteVariableName(varname)} || '`
        )
      }
      query = this.executeDynamicSQL({ sql: query, addQuotes: true })
    } else {
      for (const varname of varnames) {
        query = query.replace(
          RegExp(`@${varname}`, 'g'),
          this.quoteVariableName(varname)
        )
      }
    }
    return query
  }

  paramTypeToDataProviderType(
    inputType: WorkflowComponentParamType,
    inputValue: string
  ) {
    switch (inputType) {
      case WorkflowComponentParamType.String:
        return 'TEXT'
      case WorkflowComponentParamType.Number:
        return isNumericIntegerValue(inputValue) ? 'INT' : 'FLOAT'
      case WorkflowComponentParamType.Boolean:
        return 'BOOLEAN'
      default:
        return 'TEXT'
    }
  }

  columnTypeToDataProviderType(colType: ColumnDataType) {
    switch (colType) {
      case ColumnDataType.String:
        return 'TEXT'
      case ColumnDataType.Number:
        return 'FLOAT'
      case ColumnDataType.Boolean:
        return 'BOOLEAN'
      case ColumnDataType.Geometry:
        return 'GEOMETRY'
      case ColumnDataType.Binary:
        return 'BYTEA'
      default:
        return 'TEXT'
    }
  }

  wrapCodeInProcedure(
    name: string,
    workflow: Workflow,
    body: string,
    includeDiagram: boolean,
    isImmutable: boolean
  ): string {
    const procedureParams = workflow.variables
      .filter((v) => v.public)
      .map(
        (input) =>
          `${this.quoteVariableName(
            input.name
          )} ${this.paramTypeToDataProviderType(input.type, input.value)}`
      )
    const hasOutput = workflow.hasStoredProcedureOutputTable()
    if (hasOutput) {
      procedureParams.push(
        `${WORKFLOW_OUTPUT_TABLE_NAME_PARAMETER_NAME} INOUT TEXT`
      )
    }
    const declareVariables = workflow.variables
      .filter((v) => !v.public)
      .map(
        (input) =>
          `${this.quoteVariableName(
            input.name
          )} ${this.paramTypeToDataProviderType(
            input.type,
            input.value
          )} DEFAULT ${formatVariableValue(input)};`
      )
    let outputTableVars = ''
    if (hasOutput || isImmutable) {
      declareVariables.push(`${OUTPUT_TABLE_NAME_VAR_NAME} TEXT;`)
      declareVariables.push(`${OUTPUT_TABLE_FQN_VAR_NAME} TEXT;`)
      const outputTableNameVariable = `${OUTPUT_TABLE_NAME_VAR_NAME} := ${this.getStoredProcedureOutputTableNameExpression(
        workflow,
        false
      )};`
      const outputTableFqnVariable = `${OUTPUT_TABLE_FQN_VAR_NAME} := ${this.getStoredProcedureOutputTableNameExpression(
        workflow,
        true
      )};`
      outputTableVars = dedent`${outputTableNameVariable}
                               ${outputTableFqnVariable}`
    }
    const defaultValues = workflow.variables
      .filter((v) => v.public)
      .map(
        (input) =>
          `${this.quoteVariableName(
            input.name
          )} := COALESCE(${this.quoteVariableName(
            input.name
          )}, ${formatVariableValue(input)});`
      )
    const comment = this._getStoredProcedureEmbeddedComment(
      workflow,
      isImmutable
    )
    const returnTableName = hasOutput
      ? dedent`
    ${WORKFLOW_OUTPUT_TABLE_NAME_PARAMETER_NAME} := ${OUTPUT_TABLE_FQN_VAR_NAME};`
      : ''
    return dedent`
                CREATE OR REPLACE PROCEDURE
                  ${this.tempStoragePath}.${name}(
                    ${procedureParams.join(',\n')}
                  )
                  LANGUAGE plpgsql
                  AS
                  $body$
                  ${includeDiagram ? comment : ''}
                  ${
                    declareVariables.length > 0
                      ? dedent`
                        DECLARE
                          ${declareVariables.join('\n')}`
                      : ''
                  }
                  BEGIN
                    ${defaultValues.length > 0 ? defaultValues.join('\n') : ''}
                    ${outputTableVars}
                    ${body}
                    ${returnTableName}
                  END;
                  $body$`
  }

  getCheckStoredProcedureExistsQueries(
    workflow: Workflow,
    isImmutable: boolean
  ) {
    const versionId = workflow.workflowVersionHash()
    const paramsId = workflow.workflowParamsHash()
    const schema = this.tempStoragePath.split('.')?.[1]
    const storedProcedureName = workflow.getStoredProcedureName()
    const procName = `_temp_proc_check_${hashString(workflow.id)}_${versionId}`
    const proc = dedent`
      DECLARE
        procedureExists BOOLEAN;
        isCurrentVersion BOOLEAN;
      BEGIN
        SELECT COUNT(*) > 0 INTO procedureExists
        FROM pg_catalog.pg_proc
        JOIN pg_namespace ON pg_catalog.pg_proc.pronamespace = pg_namespace.oid
        WHERE proname = '${storedProcedureName}'
          AND pg_namespace.nspname = '${schema}';
        IF (procedureExists) THEN
          SELECT TRUE
          INTO isCurrentVersion
          FROM pg_catalog.pg_proc
          JOIN pg_namespace ON pg_catalog.pg_proc.pronamespace = pg_namespace.oid
          WHERE proname = '${storedProcedureName}'
            AND pg_namespace.nspname = '${schema}'
            AND position('"versionId":"${versionId}"' in prosrc) > 0
            AND position('"paramsId":"${paramsId}"' in prosrc) > 0
            AND position('"isImmutable":${isImmutable}' in prosrc) > 0;
          IF (isCurrentVersion) THEN
            SELECT 'exists' INTO status;
          ELSE
            SELECT 'outdated' INTO status;
          END IF;
        ELSE
          SELECT 'missing' INTO status;
        END IF;
      END;
    `
    // Postgres procedures only allow INOUT parameters, not OUT
    return [
      dedent`CREATE OR REPLACE PROCEDURE ${procName}(status INOUT TEXT)
      AS $$
        ${proc}
      $$ LANGUAGE plpgsql;`,
      `CALL ${procName}('');`,
      `DROP PROCEDURE ${procName}(TEXT);`
    ]
  }

  getRemoveStoredProcedureQuery(workflow: Workflow): string {
    const procedureParams = workflow.variables
      .filter((v) => v.public)
      .map(
        (input) =>
          `${this.paramTypeToDataProviderType(input.type, input.value)}`
      )
    if (workflow.hasStoredProcedureOutputTable()) {
      // Param _outputtablename INOUT TEXT
      procedureParams.push('TEXT')
    }
    return dedent`DROP PROCEDURE IF EXISTS ${this.getStoredProcedureFullName(
      workflow
    )}(${procedureParams.join()})`
  }

  extractErrorLineNumberFromApiResponse(response: string): number | null {
    const match = response.match(/line (\d+) at/)
    if (match) {
      const OFFSET = 5 // offset caused by header comment and procedure definition
      return parseInt(match[1]) + OFFSET
    } else {
      return null
    }
  }

  getSourceTablePermissionsQuery(workflow: Workflow) {
    const conditions: Record<string, Array<string>> = {}
    const tablenames: Array<string> = []
    const selects: Array<string> = []
    for (const [componentName, component] of Object.entries(
      workflow.components
    )) {
      if (component.metadata.name === new ReadTable().metadata.name) {
        const value = workflow.componentValues[componentName]['source']
        const tablename = (value as WorkflowComponentValue).value as string
        tablenames.push(tablename)
        const [catalog, schema, name] = tablename.toLowerCase().split('.')
        if (!(catalog in conditions)) {
          conditions[catalog] = []
        }
        conditions[catalog].push(
          dedent`(TABLE_CATALOG = '${this.unquoteName(
            catalog
          )}' AND TABLE_SCHEMA = '${schema}' AND TABLE_NAME = '${name}')`
        )
        tablename
      }
    }
    if (tablenames.length === 0) {
      return ''
    }
    const accessible = Object.keys(conditions)
      .map((k) => {
        return dedent`SELECT TABLE_CATALOG || '.' || TABLE_SCHEMA || '.' || TABLE_NAME AS name
                FROM ${k}.INFORMATION_SCHEMA.TABLE_PRIVILEGES
                WHERE ${conditions[k].join('\nOR\n')}`
      })
      .join('\nUNION ALL\n')

    const query = dedent`WITH _accessible AS (
              ${accessible}
            ),
            _names AS (
              ${tablenames
                .map((x) => dedent`SELECT '${this.unquoteName(x)}' AS name`)
                .join('\nUNION ALL\n')}
            )
            SELECT ARRAY_AGG(name) AS noAccess
            FROM _names n
            WHERE NOT EXISTS (
              SELECT 1
              FROM _accessible a
              WHERE n.name = a. name
            );`
    return query
  }

  getTempStorageSetupQueries(options?: any) {
    const schema = this.tempStoragePath.split('.')?.[1]
    const procName = `_temp_proc_${hashString(this.tempStoragePath)}`
    const proc = dedent`
      DECLARE
        schemaExists BOOL;
      BEGIN
        SELECT COUNT(*) != 0 INTO schemaExists FROM pg_namespace WHERE nspname = '${schema}';
        IF (schemaExists) THEN
          BEGIN
            CREATE TABLE IF NOT EXISTS ${schema}.__testtable AS (SELECT 1 AS N);
            DROP TABLE IF EXISTS ${schema}.__testtable;
            SELECT '{"success": true}' INTO result;
          EXCEPTION WHEN OTHERS THEN
            SELECT '{"success": false, "error": "temp_storage_create_tables"}' INTO result;
          END;
        ELSE
          BEGIN
            CREATE SCHEMA IF NOT EXISTS ${schema};
            SELECT '{"success": true}' INTO result;
          EXCEPTION WHEN OTHERS THEN
            SELECT '{"success": false, "error": "temp_storage_access_create"}' INTO result;
          END;
        END IF;
      END;
    `
    // Postgres procedures only allow INOUT parameters, not OUT
    return [
      dedent`CREATE OR REPLACE PROCEDURE ${procName}(result INOUT TEXT)
      AS $$
        ${proc}
      $$ LANGUAGE plpgsql;`,
      `CALL ${procName}('');`,
      `DROP PROCEDURE ${procName}(TEXT);`
    ]
  }

  getTempStorageCleanUpQuery(): string | null {
    const schema = this.tempStoragePath.split('.')[1]
    return dedent`
      DO $$
        DECLARE
          drops TEXT;
        BEGIN
          SELECT
            STRING_AGG(
              'DROP TABLE IF EXISTS ' || tablename::text || ';'
              'DELETE FROM ${schema}.table_creation_time WHERE tablename = ''' || tablename::text || '''',
              ';'
            ) drops
          INTO
            drops
          FROM
            ${schema}.table_creation_time
          WHERE
            tablename LIKE '%${schema}.workflow\_%' AND
            EXTRACT(epoch FROM current_timestamp - timestamp)/86400 > 30;
          IF drops IS NOT NULL THEN
            EXECUTE drops;
          END IF;
        END
      $$;`
  }

  getStoredProcedureCacheInvalidationQueries(
    workflow: Workflow
  ): Array<string> {
    const [database, schema] = this.tempStoragePath.split('.')
    return [
      dedent`
    DO $$
      DECLARE
        drops TEXT;
      BEGIN
        SELECT
          STRING_AGG(
            'DROP TABLE IF EXISTS ${
              this.tempStoragePath
            }.' || table_name::text || ';', ''
          ) drops
        INTO
          drops
        FROM
          ${database}.information_schema.tables
        WHERE
          table_schema = '${schema}'
          AND table_name ILIKE '${workflow.getStoredProcedureOutputTableName(
            this
          )}%';
        IF drops IS NOT NULL THEN
          EXECUTE drops;
        END IF;
      END
    $$;`
    ]
  }

  getTableListQuery(filter: string): string | null {
    const [database, schema] = this.tempStoragePath.split('.')
    filter = this.applyCapitalization(filter)
    return dedent`
      SELECT table_name
      FROM "${database}".information_schema.tables
      WHERE table_schema = '${schema}' AND table_name LIKE '${filter}';`
  }

  dataProviderTypeToColumnType(type: string): ColumnDataType {
    switch (type) {
      case 'INT2':
      case 'INT4':
      case 'INT8':
      case 'SERIAL2':
      case 'SERIAL4':
      case 'SERIAL8':
      case 'FLOAT4':
      case 'FLOAT8':
      case 'NUMERIC':
        return ColumnDataType.Number
      case 'BOOL':
        return ColumnDataType.Boolean
      case 'GEOMETRY':
        return ColumnDataType.Geometry
      case 'GEOGRAPHY':
        return ColumnDataType.Geography
      case 'DATE':
        return ColumnDataType.Date
      case 'TIME':
      case 'TIMETZ':
        return ColumnDataType.Time
      case 'TIMESTAMP':
      case 'TIMESTAMPTZ':
        return ColumnDataType.Timestamp
      case 'BYTEA':
        return ColumnDataType.Binary
      default:
        return ColumnDataType.String
    }
  }

  quoteName(name: string) {
    if (name) {
      const parts = name.split('.')
      if (parts.length === 3) {
        name = `"${parts[0].replace(/"/g, '')}".${parts[1]}.${parts[2]}`
      }
    }
    return name
  }

  applyCapitalization(tablename: string): string {
    return tablename.toLowerCase()
  }

  adaptComponent(component: WorkflowComponent) {
    const whiteList = ['native.geographytogeometry']
    if (!whiteList.includes(component.metadata.name)) {
      for (const input of component.inputs) {
        if (
          input.type === WorkflowComponentParamType.Column &&
          input.dataType
        ) {
          input.dataType = (input.dataType as Array<ColumnDataType>).filter(
            (x) => x !== ColumnDataType.Geography
          )
        }
      }
    }
    component.inputs = component.inputs.filter(
      (x) => x.providers == null || x.providers.includes(Provider.Postgres)
    )
  }

  getOutputSchemasQueries(tableNames: Array<string>): string {
    const parts = this.tempStoragePath.split('.')
    const database = parts[0]
    const schema = parts[1]
    return dedent`
      WITH cols AS (
        SELECT
          table_name,
          column_name AS name,
          udt_name AS db_type
        FROM "${database}".information_schema.columns
        WHERE
          table_name IN (${tableNames.map((t) => `'${t}'`).join(', ')})
          AND table_schema = '${schema}'
        ORDER BY ordinal_position ASC
      ),
      cols_with_types AS (
        SELECT
          *,
          CASE LOWER(db_type)
            WHEN 'int2' THEN '${ColumnDataType.Number}'
            WHEN 'int4' THEN '${ColumnDataType.Number}'
            WHEN 'int8' THEN '${ColumnDataType.Number}'
            WHEN 'serial2' THEN '${ColumnDataType.Number}'
            WHEN 'serial4' THEN '${ColumnDataType.Number}'
            WHEN 'serial8' THEN '${ColumnDataType.Number}'
            WHEN 'float4' THEN '${ColumnDataType.Number}'
            WHEN 'float8' THEN '${ColumnDataType.Number}'
            WHEN 'numeric' THEN '${ColumnDataType.Number}'
            WHEN 'bool' THEN '${ColumnDataType.Boolean}'
            WHEN 'geometry' THEN '${ColumnDataType.Geometry}'
            WHEN 'geography' THEN '${ColumnDataType.Geography}'
            WHEN 'date' THEN '${ColumnDataType.Date}'
            WHEN 'time' THEN '${ColumnDataType.Time}'
            WHEN 'timetz' THEN '${ColumnDataType.Time}'
            WHEN 'timestamp' THEN '${ColumnDataType.Timestamp}'
            WHEN 'timestamptz' THEN '${ColumnDataType.Timestamp}'
            WHEN 'bytea' THEN '${ColumnDataType.Binary}'
            ELSE '${ColumnDataType.String}'
          END AS type
        FROM cols
      )
      SELECT
        CONCAT('${this.tempStoragePath}.', table_name) AS fqn,
        '[' ||
          STRING_AGG(
            '{"name":"' || name || '","type":"' || type || '","db_type":"' || db_type || '"}',
            ','
          )
        || ']'
        AS schema
      FROM cols_with_types
      GROUP BY table_name;`
  }

  prepareSchemaQueries(queries: Array<string>): Array<string> {
    const last = queries.pop() as string
    return [
      dedent`
      DO
      $$
        BEGIN
          ${queries.join('\n')}
        END;
      $$`,
      last
    ]
  }

  getGeoDataType(): ColumnDataType {
    return ColumnDataType.Geometry
  }
}
